import React, { useState, useEffect, useRef } from 'react';
import { View, Text, TextInput, Platform, StyleSheet, Image, TouchableOpacity, ActivityIndicator, Alert } from 'react-native';
import { useNavigation, useRoute } from '@react-navigation/native';
import { signInWithEmailAndPassword, createUserWithEmailAndPassword, updateProfile, getAuth } from 'firebase/auth';
import { getFirestore, collection, query, where, getDocs, limit, doc, setDoc, getDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getResponsiveValue } from '../styles/breakpoints';
import { useTypingAnimation } from '../hooks/useTypingAnimation';
import { useUser } from './LoginPersistence';
import { createOnceAsyncLock } from '../utils/onceAsync';

const DEFAULT_PROFILE_PHOTOS = [
  'https://firebasestorage.googleapis.com/v0/b/venue-report-card.firebasestorage.app/o/userProfilePhotos%2Fkoi.png?alt=media&token=ee0cda3e-781a-4d2d-9bbe-8df851ede3fa',
  'https://firebasestorage.googleapis.com/v0/b/venue-report-card.firebasestorage.app/o/userProfilePhotos%2Fbear.png?alt=media&token=ee0cda3e-781a-4d2d-9bbe-8df851ede3fa'
];

const getRandomDefaultPhoto = () => {
  return DEFAULT_PROFILE_PHOTOS[Math.floor(Math.random() * DEFAULT_PROFILE_PHOTOS.length)];
};

// Password validation rules
const PASSWORD_REQUIREMENTS = {
  minLength: 7,
  maxLength: 20,
};

const validatePassword = (password) => {
  const errors = [];
  
  if (password.length < PASSWORD_REQUIREMENTS.minLength) {
    errors.push(`Password must be at least ${PASSWORD_REQUIREMENTS.minLength} characters long`);
  }
  if (password.length >= PASSWORD_REQUIREMENTS.maxLength) {
    errors.push(`Password must be less than ${PASSWORD_REQUIREMENTS.maxLength} characters`);
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

// Username validation
const OFFENSIVE_TERMS = [
  'offensive', 'slur', 'inappropriate', 'profanity',
];

const validateUsername = async (username) => {
  const errors = [];
  
  if (username.length <= 6) {
    errors.push('Username must be at least 6 characters long');
    return { isValid: false, errors };
  }
  if (username.length >= 20) {
    errors.push('Username must be less than 20 characters');
    return { isValid: false, errors };
  }

  const lowercaseUsername = username.toLowerCase();
  for (const term of OFFENSIVE_TERMS) {
    if (lowercaseUsername.includes(term)) {
      errors.push('Username contains inappropriate content');
      return { isValid: false, errors };
    }
  }

  try {
    const db = getFirestore();
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('username', '==', username), limit(1));
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      errors.push('Username is already taken');
      return { isValid: false, errors };
    }
  } catch (error) {
    console.error('Error checking username availability:', error);
    errors.push('Error checking username availability');
    return { isValid: false, errors };
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

const AuthInput = ({ label, value, onChangeText, placeholder, secureTextEntry, showPlaceholder = false, color = '#e9e9e7', onSubmitEditing }) => {
  const [isFocused, setIsFocused] = useState(false);

  return (
    <View 
      onClick={() => {
        if (Platform.OS === 'web') {
          const input = document.querySelector(`input[placeholder="${placeholder}"]`);
          if (input) input.focus();
        }
      }} 
      style={styles.inputRow}
    >
      <Text 
        style={[
          styles.authInputLabel, 
          { 
            fontSize: label.length > 12 
              ? getResponsiveValue({
                mobile: 16,
                tablet: 18,
                laptop: 20,
                desktop: 20,
              })
              : getResponsiveValue({
                mobile: 20,
                tablet: 22,
                laptop: 24,
                desktop: 24,
              }), 
          }
        ]}
      >
        {label}
      </Text>
      <TextInput 
        onBlur={() => setIsFocused(false)} 
        onFocus={() => setIsFocused(true)} 
        id={label} 
        style={styles.authInput} 
        value={value} 
        onChangeText={onChangeText} 
        onSubmitEditing={onSubmitEditing}
        placeholder={placeholder} 
        placeholderTextColor={showPlaceholder && !isFocused ? '#8f8c8a' : 'transparent'} 
        secureTextEntry={secureTextEntry} 
        returnKeyType={secureTextEntry || label.toLowerCase().includes('password') ? 'done' : 'next'}
      />
    </View>
  );
};

export const CommunityAuthForm = ({ 
  isModal = false,
  onClose = () => {},
}) => {
  const navigation = useNavigation();
  const route = useRoute();
  const auth = getAuth();
  const { setUser, validateInviteCode, claimInviteCode } = useUser();

  const [formData, setFormData] = useState({
    emailLogin: '',
    passwordLogin: '',
    inviteCode: '',
    email: '',
    password: '',
    confirmPassword: '',
    username: '',
  });

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const registerLock = useRef(createOnceAsyncLock()).current;

  // Helper function to get inviteCode from URL parameters
  const getInviteCodeFromRoute = () => {
    // Check route params first (for mobile/navigation)
    if (route?.params?.inviteCode) {
      return route.params.inviteCode;
    }

    // Check URL search params (for web)
    if (Platform.OS === 'web' && typeof window !== 'undefined' && window.location) {
      const urlParams = new URLSearchParams(window.location.search);
      return urlParams.get('inviteCode');
    }

    return null;
  };

  // Check for inviteCode in route and populate the field
  useEffect(() => {
    const inviteCodeFromRoute = getInviteCodeFromRoute();
    if (inviteCodeFromRoute) {
      // Only set if field is empty to avoid overwriting user input
      setFormData(prev => {
        if (!prev.inviteCode) {
          return { ...prev, inviteCode: inviteCodeFromRoute.toUpperCase() };
        }
        return prev;
      });
    }
  }, [route?.params?.inviteCode]);

  // Also check on mount for web URL parameters (in case they're not in route params)
  useEffect(() => {
    if (Platform.OS === 'web') {
      const inviteCodeFromRoute = getInviteCodeFromRoute();
      if (inviteCodeFromRoute) {
        setFormData(prev => {
          if (!prev.inviteCode) {
            return { ...prev, inviteCode: inviteCodeFromRoute.toUpperCase() };
          }
          return prev;
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  const ROLES = [
    'Live Performer',
    'Venue Employee',
    'Touring Crew',
    'Backline Tech',
    'Booking Agent',
    'Promoter',
    'Venue Manager',
    'Tour Manager',
    'LD',
    'FOH',
  ];


  const animatedRolePlaceholder = useTypingAnimation(ROLES, 100, 50, 2000);

  const handleFieldChange = (field, value) => {
    const updatedFormData = {
      ...formData,
      [field]: value
    };
    setFormData(updatedFormData);
    setError(''); // Clear error when user types
  };

  const handleLogin = async () => {
    setError('');
    setLoading(true);

    try {
      if (!formData.emailLogin || !formData.passwordLogin) {
        setError('Please enter both email and password');
        setLoading(false);
        return;
      }

      const userCredential = await signInWithEmailAndPassword(
        auth,
        formData.emailLogin,
        formData.passwordLogin
      );

      // Get user data from Firestore
      const db = getFirestore();
      const userDoc = await getDoc(doc(db, 'users', userCredential.user.uid));
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        setUser({ ...userData, uid: userCredential.user.uid });
        
        // Redirect to CommunityList on successful login
        navigation.replace('CommunityList');
      } else {
        setError('User data not found');
        setLoading(false);
      }
    } catch (err) {
      console.error('Login error:', err);
      let errorMessage = 'Login failed';
      
      if (err.code === 'auth/user-not-found') errorMessage = 'No account found with this email';
      else if (err.code === 'auth/wrong-password') errorMessage = 'Incorrect password';
      else if (err.code === 'auth/invalid-email') errorMessage = 'Invalid email format';
      else if (err.code === 'auth/user-disabled') errorMessage = 'This account has been disabled';
      else if (err.code === 'auth/too-many-requests') errorMessage = 'Too many failed attempts. Please try again later';
      else if (err.message) errorMessage = err.message;
      
      setError(errorMessage);
      setLoading(false);
    }
  };

  const handleRegister = async () => {
    await registerLock.run(async () => {
    setError('');
    setLoading(true);

    try {
      // Validate all required fields
      if (!formData.username || !formData.email || !formData.password || !formData.confirmPassword) {
        setError('Please fill in all required fields');
        setLoading(false);
        return;
      }

      // Password validation
      if (formData.password !== formData.confirmPassword) {
        setError('Passwords do not match');
        setLoading(false);
        return;
      }

      const passwordValidation = validatePassword(formData.password);
      if (!passwordValidation.isValid) {
        setError(passwordValidation.errors.join('\n'));
        setLoading(false);
        return;
      }

      // Username validation
      const usernameValidation = await validateUsername(formData.username);
      if (!usernameValidation.isValid) {
        setError(usernameValidation.errors.join('\n'));
        setLoading(false);
        return;
      }

      // Email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        setError('Please enter a valid email address');
        setLoading(false);
        return;
      }

      // Validate invite code (required for registration in CommunityAuthForm)
      // Checks both inviteUrls (admin-made) and invite_codes (user-made)
      let inviteCodeId = null;
      let inviteCodeType = null;
      let isCommunityMember = false;
      
      if (!formData.inviteCode || !formData.inviteCode.trim()) {
        setError('Invite code is required for registration');
        setLoading(false);
        return;
      }

      // Validate invite code (read-only check, no auth needed)
      const codeValidation = await validateInviteCode(formData.inviteCode.trim().toUpperCase());
      if (!codeValidation.valid) {
        setError(codeValidation.error);
        setLoading(false);
        return;
      }
      inviteCodeId = codeValidation.codeId;
      inviteCodeType = codeValidation.codeType;
      isCommunityMember = true;

      // Create user account
      const userCredential = await createUserWithEmailAndPassword(auth, formData.email, formData.password);
      const displayName = formData.username;
      const photoURL = getRandomDefaultPhoto();
      
      await updateProfile(userCredential.user, { displayName, photoURL });

      const db = getFirestore();
      const userData = {
        username: formData.username,
        email: formData.email.toLowerCase(),
        photoURL: photoURL,
        folders: {},
        files: {},
        status: 'active',
        userType: 'user',
        createdAt: new Date(),
        submissions: 0,
        isPublic: false,
        isCommunityMember,
        registrationMessage: isCommunityMember 
          ? 'Welcome to Venue Report Card! Your account has been activated with community member access.'
          : 'Welcome to Venue Report Card! We are currently in closed alpha. Your account has been added to our waitlist and will be activated soon.',
      };

      await setDoc(doc(db, 'users', userCredential.user.uid), userData);

      // Claim the invite code now that user is authenticated
      if (inviteCodeId) {
        const claimResult = await claimInviteCode(inviteCodeId, userCredential.user.uid, inviteCodeType);
        if (!claimResult.success) {
          console.error('Failed to claim invite code:', claimResult.error);
          // Don't block registration if claiming fails, but log it
        }
      }

      // Send registration email notification
      try {
        const functions = getFunctions();
        const handleNewRegistration = httpsCallable(functions, 'handleNewRegistration');
        await handleNewRegistration({
          ...userData,
          uid: userCredential.user.uid
        });
      } catch (emailError) {
        console.error('Error sending registration emails:', emailError);
        // Don't block registration if email fails
      }

      setUser({ ...userData, uid: userCredential.user.uid });
      
      // Redirect to CommunityList on successful registration
      navigation.replace('CommunityList');
    } catch (err) {
      console.error('Registration error:', err);
      let errorMessage = 'Registration failed';
      
      if (err.code === 'auth/email-already-in-use') errorMessage = 'Email already registered';
      else if (err.code === 'auth/invalid-email') errorMessage = 'Invalid email format';
      else if (err.code === 'auth/weak-password') errorMessage = 'Password is too weak';
      else if (err.message) errorMessage = err.message;
      
      setError(errorMessage);
      setLoading(false);
    }
    });
  };


  return (
    <View style={styles.formContainer}>
      {isModal && (
        <TouchableOpacity style={styles.closeButton} onPress={() => onClose()}>
          <Text style={styles.closeButtonText}>x</Text>
        </TouchableOpacity>
      )}
      <View style={styles.welcomeSection}>
        <Text style={styles.appName}>VENUE REPORT CARD</Text>
        <Image
          source={require('../../assets/logos/logo.png')}
          style={styles.authLogo}
        />
        <Text style={styles.appName}>COMMUNITY DIRECTORY</Text>

      </View>
      <View style={styles.formContent}>
            <AuthInput
              label="Email"
              value={formData.emailLogin || ''}
              placeholder="Enter Email"
              onChangeText={(text) => handleFieldChange('emailLogin', text)}
              onSubmitEditing={handleLogin}
            />
            <AuthInput
              label="Password"
              placeholder="Enter Password"
              value={formData.passwordLogin || ''}
              onChangeText={(text) => handleFieldChange('passwordLogin', text)}
              onSubmitEditing={handleLogin}
              secureTextEntry
            />
            <View style={styles.submitButtonWrapper}>
              <TouchableOpacity 
                style={[
                  styles.submitButtonContainer,
                  loading && styles.disabledButton
                ]} 
                onPress={handleLogin}
                disabled={loading}
              >
                {loading ? (
                  <ActivityIndicator size="small" color="#1075ae" />
                ) : (
                  <Text style={styles.submitButtonText}>LOG IN</Text>
                )}
              </TouchableOpacity>
            </View>
            <AuthInput
              label="Invite Code"
              value={formData.inviteCode || ''}
              placeholder="Enter Invite Code"
              onChangeText={(text) => handleFieldChange('inviteCode', text)}
            />
            <AuthInput
              label="Your Email"
              value={formData.email || ''}
              placeholder="Enter Email"
              onChangeText={(text) => handleFieldChange('email', text)}
            />
            <AuthInput
              label="Username"
              value={formData.username || ''}
              placeholder="Enter Username"
              onChangeText={(text) => handleFieldChange('username', text)}
            />
            <AuthInput
              label="Password"
              value={formData.password || ''}
              placeholder="Enter Password"
              onChangeText={(text) => handleFieldChange('password', text)}
              secureTextEntry
            />
            <AuthInput
              label="Confirm Password"
              value={formData.confirmPassword || ''}
              placeholder="Enter Password"
              onChangeText={(text) => handleFieldChange('confirmPassword', text)}
              onSubmitEditing={handleRegister}
              secureTextEntry
            />
            <View style={styles.submitButtonWrapper}>
              <TouchableOpacity 
                style={[
                  styles.submitButtonContainer,
                  loading && styles.disabledButton
                ]} 
                onPress={handleRegister}
                disabled={loading}
              >
                {loading ? (
                  <ActivityIndicator size="small" color="#1075ae" />
                ) : (
                  <Text style={styles.submitButtonText}>SIGN UP</Text>
                )}
              </TouchableOpacity>
            </View>
            
            {/* Error Display */}
            {error && (
              <View style={styles.errorContainer}>
                <Text style={styles.errorText}>{error}</Text>
              </View>
            )}
            

      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  formContainer: {
    position: 'relative',
    width: '100%',
    maxWidth: getResponsiveValue({
      mobile: '100%',
      tablet: 400,
      laptop: 500,
      desktop: 500
    }),
    alignSelf: 'center',
    backgroundColor: '#feffe4',
    borderRadius: getResponsiveValue({
      mobile: 30,
      tablet: 35,
      laptop: 40,
      desktop: 50
    }),
    paddingVertical: getResponsiveValue({
      mobile: 20,
      tablet: 25,
      laptop: 30,
      desktop: 35
    }),
    paddingHorizontal: getResponsiveValue({
      mobile: 30,
      tablet: 40,
      laptop: 45,
      desktop: 50
    }),
    marginTop: getResponsiveValue({
      mobile: 20,
      tablet: 25,
      laptop: 30,
      desktop: 30
    }),
    ...(Platform.OS === 'web' && {
      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
    }),
  },
  formContent: {
    width: '100%',
  },
  inputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 3,
    gap: 15,
    width: '100%',
    cursor: 'text',
  },
  authInputLabel: {
    color: '#8f8c8a',
    fontFamily: 'VastagoGrotesk',
    fontWeight: '400',
    whiteSpace: 'nowrap',
    paddingTop: getResponsiveValue({
      mobile: 10,
      tablet: 10,
      laptop: 4,
      desktop: 4
    }),
  },
  authInput: {
    borderBottomWidth: 2,
    borderBottomColor: '#8f8c8a',
    borderStyle: 'solid', // this is needed for web
    width: '100%',
    color: '#8f8c8a',
    fontFamily: 'Computer Says No',
    fontWeight: '400',
    paddingLeft: 8,
    fontSize: Platform.OS === 'web' ? 28 : 22,
    ...Platform.select({
      web: {
        outlineStyle: 'none',
      },
    }),
  },
  welcomeSection: {
    alignItems: 'center',
    marginBottom: getResponsiveValue({
      mobile: 5,
      tablet: 5,
      laptop: 20,
      desktop: 20
    }),
  },
  appName: {
    fontFamily: 'Playpen Sans',
    fontWeight: '500',
    fontSize: getResponsiveValue({
      mobile: 18,
      tablet: 18,
      laptop: 20,
      desktop: 24
    }),
    color: '#578dff',
  },
  authLogo: {
    width: Platform.OS === 'web' ? 100 : 80,
    height: Platform.OS === 'web' ? 100 : 80,
    // remove deprecated style-based resizeMode; set via prop where used
  },
  submitButtonWrapper: {
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
  },
  submitButtonContainer: {
    backgroundColor: '#dfffdd',
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 5,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 10,
    width: 'auto',
  },
  submitButtonText: {
    color: '#1075ae',
    fontFamily: 'Playpen Sans',
    fontSize: 16,
    fontWeight: '700',
  },
  disabledButton: {
    opacity: 0.6,
  },
  errorContainer: {
    marginTop: 15,
    padding: 12,
    backgroundColor: 'rgba(231, 76, 60, 0.1)',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: 'rgba(231, 76, 60, 0.3)',
  },
  errorText: {
    color: '#e74c3c',
    fontFamily: 'VastagoGrotesk',
    fontSize: 14,
    textAlign: 'center',
  },
  toggleButton: {
    marginTop: 20,
    alignItems: 'center',
  },
  toggleButtonText: {
    color: '#578dff',
    fontFamily: 'VastagoGrotesk',
    fontSize: 14,
    textDecorationLine: 'underline',
  },
  closeButton: {
    position: 'absolute',
    top: 5,
    right: 5,
    padding: 10,
    borderRadius: 15,
  },
});

export default CommunityAuthForm;

